/**
 * Topology diagram for a reference docs page (`.doc`): what a system is made of, where
 * each part runs and what talks to what. Requests flow left to right, a group box is a
 * cloud or a cluster, and a cylinder mark is something that keeps data.
 * It is laid out by a pure function (lib/docTopology.ts) and drawn with real text and the
 * page's tokens, not an image: labels wrap, every skin and mode works, and a screen
 * reader gets the connections as a list.
 * Static — the groups, nodes and edges come from the page's markdown (a `mermaid`
 * `flowchart LR` fence under a `docs:topology` directive, see /docs-writer), never from a
 * query.
 */
import type { CSSProperties, ReactNode } from 'react';
import {
  COL_UNITS,
  NODE_H,
  groupInset,
  layoutTopology,
  nodeInset,
  type DocTopoEdge,
  type DocTopoGroup,
  type DocTopoNode,
} from '../lib/docTopology';
import './DocTopology.css';

export interface DocTopologyProps {
  caption: string;
  groups?: DocTopoGroup[];
  nodes: DocTopoNode[];
  /** `text` is a short label on the line (a share, a protocol). */
  edges: (DocTopoEdge & { text?: ReactNode })[];
}

function StoreGlyph() {
  return (
    <svg className="doc-topo-store" viewBox="0 0 12 12" aria-hidden="true">
      <ellipse cx="6" cy="2.8" rx="4.4" ry="1.8" />
      <path d="M1.6 2.8v6.4c0 1 2 1.8 4.4 1.8s4.4-.8 4.4-1.8V2.8" />
      <path d="M1.6 6c0 1 2 1.8 4.4 1.8s4.4-.8 4.4-1.8" />
    </svg>
  );
}

export function DocTopology({ caption, groups = [], nodes, edges }: DocTopologyProps) {
  const layout = layoutTopology(caption, groups, nodes, edges);
  const width = layout.cols * COL_UNITS;
  const pct = (units: number) => `${(units / width) * 100}%`;
  const nodeIn = nodeInset(layout.cols);
  const groupIn = groupInset(layout.cols);
  const nameOf = new Map<string, string>([...groups, ...nodes].map((x) => [x.id, x.label]));
  const count = `${nodes.length} parts, ${edges.length} connections`;

  return (
    <figure className="doc-topo" style={{ '--topo-cols': layout.cols } as CSSProperties}>
      <figcaption className="doc-topo-cap">
        <span>{caption}</span>
        <span className="doc-topo-count">{count}</span>
      </figcaption>
      <div className="doc-topo-scroll" tabIndex={0} role="group" aria-label={`${caption}. ${count}.`}>
        <div className="doc-topo-canvas" style={{ height: layout.height }} aria-hidden="true">
          {layout.groups.map((b) => {
            const g = groups.find((x) => x.id === b.id)!;
            return (
              <div
                key={b.id}
                className="doc-topo-group"
                style={{
                  left: pct(b.colFrom * COL_UNITS + groupIn),
                  width: pct((b.colTo - b.colFrom + 1) * COL_UNITS - 2 * groupIn),
                  top: b.top,
                  height: b.height,
                }}
              >
                <span className="doc-topo-group-label">{g.label}</span>
                {g.note && <span className="doc-topo-group-note">{g.note}</span>}
              </div>
            );
          })}

          <svg className="doc-topo-edges" viewBox={`0 0 ${width} ${layout.height}`} preserveAspectRatio="none">
            {layout.edges.map((e, i) => {
              const sideways = e.mode === 'forward' || e.mode === 'backward';
              const mx = (e.x1 + e.x2) / 2;
              const d = sideways
                ? `M${e.x1} ${e.y1} C${mx} ${e.y1} ${mx} ${e.y2} ${e.x2} ${e.y2}`
                : `M${e.x1} ${e.y1} L${e.x2} ${e.y2}`;
              return <path key={i} d={d} className={edges[i]!.dashed ? 'doc-topo-edge doc-topo-edge--dashed' : 'doc-topo-edge'} />;
            })}
          </svg>

          {layout.edges.map((e, i) => (
            <span
              key={i}
              className={`doc-topo-head doc-topo-head--${e.mode}${edges[i]!.dashed ? ' doc-topo-head--dashed' : ''}`}
              style={{ left: pct(e.x2), top: e.y2 }}
            />
          ))}

          {layout.edges.map((e, i) =>
            edges[i]!.text ? (
              <span key={i} className="doc-topo-label" style={{ left: pct((e.x1 + e.x2) / 2), top: (e.y1 + e.y2) / 2 }}>
                {edges[i]!.text}
              </span>
            ) : null,
          )}

          {layout.nodes.map((p) => {
            const n = nodes.find((x) => x.id === p.id)!;
            return (
              <div
                key={p.id}
                className={`doc-topo-node${n.kind === 'store' ? ' doc-topo-node--store' : ''}`}
                style={{
                  left: pct(p.col * COL_UNITS + nodeIn),
                  width: pct(COL_UNITS - 2 * nodeIn),
                  top: p.top,
                  height: NODE_H,
                }}
              >
                <span className="doc-topo-node-label">
                  {n.kind === 'store' && <StoreGlyph />}
                  {n.label}
                </span>
                {n.note && <span className="doc-topo-node-note">{n.note}</span>}
              </div>
            );
          })}
        </div>

        {/* The same picture as text, for a screen reader and for find-in-page. */}
        <div className="doc-vh">
          <ul>
            {nodes.map((n) => (
              <li key={n.id}>
                {n.label}
                {n.note ? `, ${n.note}` : ''}
                {n.group ? `, in ${nameOf.get(n.group)}` : ''}
              </li>
            ))}
          </ul>
          <ul>
            {edges.map((e, i) => (
              <li key={i}>
                {nameOf.get(e.from)} {e.dashed ? 'uses' : 'to'} {nameOf.get(e.to)}
                {e.text ? <>: {e.text}</> : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </figure>
  );
}
